import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  CheckInIcon,
  CheckOutIcon,
  ClockOutlinedIcon,
} from "../../utilities/icons";
import {
  attendanceTodayApi,
  attendanceCheckinApi,
  attendanceCheckoutApi,
} from "../../apiCalls/Apicalls";

function formatTime(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

const STATUS_COLORS = {
  present: { bg: "rgba(34,197,94,0.1)", color: "#16a34a", label: "Present" },
  late: { bg: "rgba(251,146,60,0.1)", color: "#ea580c", label: "Late" },
  "half-day": { bg: "rgba(168,85,247,0.1)", color: "#9333ea", label: "Half Day" },
  absent: { bg: "rgba(239,68,68,0.1)", color: "#dc2626", label: "Absent" },
};

function AttendanceWidget() {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // JS getTimezoneOffset() returns minutes BEHIND UTC (negative for ahead).
  // We invert it so +330 = IST (UTC+5:30).
  const tzOffset = -new Date().getTimezoneOffset();

  const authHeaders = () => ({ headers: { Authorization: Cookies.get("jwtToken") } });

  const fetchToday = () => {
    setLoading(true);
    axios
      .get(`${attendanceTodayApi}?tzOffsetMinutes=${tzOffset}`, authHeaders())
      .then((res) => setRecord(res.data?.record || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLocation = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve({});
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }),
        () => resolve({}),
        { timeout: 8000, enableHighAccuracy: true }
      );
    });

  const handleCheckIn = async () => {
    setActionLoading(true);
    const location = await getLocation();
    try {
      const res = await axios.post(
        attendanceCheckinApi,
        { ...location, tzOffsetMinutes: tzOffset },
        authHeaders()
      );
      setRecord(res.data?.record || null);
      toast.success("Checked in successfully!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Check-in failed";
      if (err?.response?.status === 409) {
        setRecord(err.response.data?.record || record);
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = async () => {
    setActionLoading(true);
    const location = await getLocation();
    try {
      const res = await axios.post(
        attendanceCheckoutApi,
        { ...location, tzOffsetMinutes: tzOffset },
        authHeaders()
      );
      setRecord(res.data?.record || null);
      toast.success("Checked out successfully!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Check-out failed";
      if (err?.response?.status === 409) {
        setRecord(err.response.data?.record || record);
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setActionLoading(false);
    }
  };

  const hasCheckedIn = Boolean(record?.checkIn);
  const hasCheckedOut = Boolean(record?.checkOut);
  const statusStyle = STATUS_COLORS[record?.status] || STATUS_COLORS.absent;

  const formatCoords = (loc) => {
    if (!loc || loc.latitude == null) return null;
    return `${loc.latitude.toFixed(5)}, ${loc.longitude.toFixed(5)}${
      loc.accuracy ? ` (±${Math.round(loc.accuracy)}m)` : ""
    }`;
  };

  return (
    <Box
      sx={{
        bgcolor: "var(--ax-surface)",
        border: "1px solid var(--ax-border)",
        borderRadius: "var(--ax-radius-lg)",
        p: 2.5,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ClockOutlinedIcon fontSize="small" sx={{ color: "var(--ax-blue)" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--ax-ink)" }}>
            Today's Attendance
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: "0.78rem", color: "var(--ax-ink-2)" }}>
          {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
        </Typography>
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={20} sx={{ color: "var(--ax-blue)" }} />
        </Stack>
      ) : (
        <>
          <Stack direction="row" spacing={3} mb={2.5}>
            <Box>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
                Check In
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "var(--ax-ink)" }}>
                {hasCheckedIn ? formatTime(record.checkIn.time) : "—"}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
                Check Out
              </Typography>
              <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "var(--ax-ink)" }}>
                {hasCheckedOut ? formatTime(record.checkOut.time) : "—"}
              </Typography>
            </Box>
            {record?.workingHours != null && (
              <Box>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
                  Hours
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: "1rem", color: "var(--ax-ink)" }}>
                  {record.workingHours.toFixed(1)}h
                </Typography>
              </Box>
            )}
            {hasCheckedIn && (
              <Box>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "var(--ax-ink-2)", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
                  Status
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1,
                    py: 0.25,
                    bgcolor: statusStyle.bg,
                    color: statusStyle.color,
                    borderRadius: "5px",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                  }}
                >
                  {statusStyle.label}
                </Box>
              </Box>
            )}
          </Stack>

          {/* Location indicators */}
          {(hasCheckedIn || hasCheckedOut) && (
            <Stack spacing={0.5} mb={1.5}>
              {hasCheckedIn && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: formatCoords(record.checkIn?.location) ? "#16a34a" : "#94a3b8",
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.74rem", color: "var(--ax-ink-2)" }}>
                    <strong>In:</strong>{" "}
                    {formatCoords(record.checkIn?.location) || "Location not captured"}
                  </Typography>
                </Stack>
              )}
              {hasCheckedOut && (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: formatCoords(record.checkOut?.location) ? "#ea580c" : "#94a3b8",
                      flexShrink: 0,
                    }}
                  />
                  <Typography sx={{ fontSize: "0.74rem", color: "var(--ax-ink-2)" }}>
                    <strong>Out:</strong>{" "}
                    {formatCoords(record.checkOut?.location) || "Location not captured"}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}

          <Stack direction="row" spacing={1}>
            {!hasCheckedIn && (
              <Button
                variant="contained"
                size="small"
                onClick={handleCheckIn}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <CheckInIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.82rem",
                  bgcolor: "#16a34a",
                  "&:hover": { bgcolor: "#15803d" },
                }}
              >
                Check In
              </Button>
            )}
            {hasCheckedIn && !hasCheckedOut && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleCheckOut}
                disabled={actionLoading}
                startIcon={actionLoading ? <CircularProgress size={14} /> : <CheckOutIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.82rem",
                  borderColor: "#ea580c",
                  color: "#ea580c",
                  "&:hover": { borderColor: "#c2410c", bgcolor: "rgba(234,88,12,0.05)" },
                }}
              >
                Check Out
              </Button>
            )}
            {hasCheckedIn && hasCheckedOut && (
              <Typography sx={{ fontSize: "0.8rem", color: "var(--ax-ink-2)" }}>
                Attendance recorded for today
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
}

export default AttendanceWidget;
